import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Dashboard from './components/Dashboard';
import TransactionsList from './components/TransactionsList';
import Insights from './components/Insights';
import StatsDashboard from './components/StatsDashboard';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './components/Login';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Configure axios to include auth token
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'x-auth-token': token } : {};
};

function App() {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check if user is already authenticated
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setCheckingAuth(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/check`, {
        headers: { 'x-auth-token': token }
      });

      if (response.data.authenticated) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('authToken');
      }
    } catch (error) {
      localStorage.removeItem('authToken');
    } finally {
      setCheckingAuth(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const [transactionsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/transactions`, { headers }),
        axios.get(`${API_URL}/api/summary`, { headers })
      ]);
      setTransactions(transactionsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      // If unauthorized, redirect to login
      if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const handleLogin = (token) => {
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        headers: getAuthHeaders()
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      setIsAuthenticated(false);
      setTransactions([]);
      setSummary(null);
    }
  };

  // Show loading while checking authentication
  if (checkingAuth) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading your financial data...</p>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="app">
        <header className="app-header">
        <h1>mado_finance</h1>
        <nav className="nav-tabs">
          <button
            className={activeTab === 'dashboard' ? 'active' : ''}
            onClick={() => setActiveTab('dashboard')}
          >
            status
          </button>
          <button
            className={activeTab === 'stats' ? 'active' : ''}
            onClick={() => setActiveTab('stats')}
          >
            stats
          </button>
          <button
            className={activeTab === 'transactions' ? 'active' : ''}
            onClick={() => setActiveTab('transactions')}
          >
            log
          </button>
          <button
            className={activeTab === 'insights' ? 'active' : ''}
            onClick={() => setActiveTab('insights')}
          >
            insights
          </button>
        </nav>
        <div className="header-actions">
          <button className="refresh-btn" onClick={fetchData}>
            refresh
          </button>
          <button className="logout-btn" onClick={handleLogout}>
            logout
          </button>
        </div>
      </header>

      <main className="app-main">
        {activeTab === 'dashboard' && summary && (
          <Dashboard transactions={transactions} summary={summary} />
        )}
        {activeTab === 'transactions' && (
          <TransactionsList transactions={transactions} />
        )}
        {activeTab === 'insights' && summary && (
          <Insights transactions={transactions} summary={summary} />
        )}
        {activeTab === 'stats' && summary && (
          <StatsDashboard transactions={transactions} summary={summary} />
        )}
      </main>
      </div>
    </ErrorBoundary>
  );
}

export default App;
